import {
  createContext,
  useCallback,
  type ReactNode,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";

import type {
  ConsumerAccountKind,
  ConsumerCompanyRegistrationSnapshot,
  LoginWithPasswordResponse,
  ProfessionalInvitationPayload,
  RegisterCompanyRegistrationPayload,
  SessionResponse,
  VerifyResponse,
} from "@/services/authService";
import {
  getSession,
  verify as verifyApi,
  verifyRootInvite as verifyRootInviteApi,
  completePlatformLoginOtp as completePlatformLoginOtpApi,
  completeProfessionalTotpLogin as completeProfessionalTotpLoginApi,
  completeProfessionalTotpRecoveryLogin as completeProfessionalTotpRecoveryLoginApi,
  isPlatformOtpRequiredResponse,
  isTotpRequiredResponse,
  loginWithPassword as loginWithPasswordApi,
  resendPlatformLoginOtp as resendPlatformLoginOtpApi,
  register as registerApi,
  createCompany as createCompanyApi,
  exchangeSsoCode as exchangeSsoCodeApi,
  logout as logoutApi,
  requestLoginLink as requestLoginLinkApi,
  applyVerifyResponse,
  applyImpersonateResponse,
  impersonate as impersonateApi,
  endImpersonation as endImpersonationApi,
  hasStoredToken,
  getProfessionalInvitationByToken as getProfessionalInvitationByTokenApi,
  acceptProfessionalInvite as acceptProfessionalInviteApi,
} from "@/services/authService";
import { clearAuth } from "@/services/authStorage";

function consumerFieldsFromSession(session: SessionResponse): {
  consumerAccountKind: ConsumerAccountKind | null;
  consumerCompanyRegistration: ConsumerCompanyRegistrationSnapshot | null;
  /**
   * Mirrors UserEntity.phoneVerifiedAt. Drives the post-payment "complete
   * profile" gate: a consumer with a verified phone + an avatar can have
   * their certificate requests promoted out of `pending_profile_completion`
   * so professionals see them.
   */
  phoneVerifiedAt: string | null;
} {
  return {
    consumerAccountKind: session.consumerAccountKind ?? null,
    consumerCompanyRegistration: session.consumerCompanyRegistration ?? null,
    phoneVerifiedAt: session.phoneVerifiedAt ?? null,
  };
}

/**
 * A professional whose certificate is still under manual review sees a
 * "pending review" banner and has the quoting/accepting actions gated. The
 * flip to `active` happens on the platform side (an admin approves the
 * invite), so nothing in this tab triggers a re-render — we revalidate the
 * session on tab focus and on a slow timer until the status clears, instead
 * of leaving the banner up until the next full page load.
 */
function isPendingProfessionalActivation(user: AuthUser | null): boolean {
  if (user == null) {
    return false;
  }
  return (
    (user.isNotary === true && user.notaryActivationStatus === "pending") ||
    (user.isAccountant === true && user.accountantActivationStatus === "pending")
  );
}

/** How often to re-poll /auth/session while an activation is pending. */
const PENDING_ACTIVATION_POLL_MS = 60 * 1000;

/** Maps a GET /auth/session payload onto the context's user shape. */
function authUserFromSession(session: SessionResponse): AuthUser {
  return {
    id: session.userId,
    email: session.email ?? "",
    displayName: session.displayName ?? null,
    firstName: session.firstName ?? null,
    lastName: session.lastName ?? null,
    secondName: session.secondName ?? null,
    secondLastName: session.secondLastName ?? null,
    documentType: session.documentType ?? null,
    documentNumber: session.documentNumber ?? null,
    identityVerifiedAt: session.identityVerifiedAt ?? null,
    profileImage: session.profileImage ?? null,
    role: session.role,
    tenantId: session.activeTenantId,
    tenantName: session.tenantName,
    tenantProfileImage: session.tenantProfileImage ?? null,
    language: session.language,
    industry: session.industry,
    profession: session.profession ?? null,
    platformRole: session.platformRole,
    isNotary: session.isNotary ?? false,
    isAccountant: session.isAccountant ?? false,
    notaryActivationStatus: session.notaryActivationStatus ?? null,
    accountantActivationStatus: session.accountantActivationStatus ?? null,
    sessionId: session.sessionId,
    onboardingCompletedAt: session.onboardingCompletedAt ?? null,
    onboardingMode: session.onboardingMode ?? null,
    hasDgiCredentials: session.hasDgiCredentials ?? false,
    emailVerifiedAt: session.emailVerifiedAt ?? null,
    phone: session.phone ?? null,
    ...consumerFieldsFromSession(session),
    hasPassword: session.hasPassword ?? true,
  };
}

export interface AuthUser {
  id: string;
  email: string;
  displayName: string | null;
  firstName: string | null;
  lastName: string | null;
  secondName: string | null;
  secondLastName: string | null;
  documentType: "national_id" | "passport" | null;
  documentNumber: string | null;
  identityVerifiedAt: string | null;
  profileImage: string | null;
  role: string;
  tenantId: string | null;
  tenantName: string | null;
  tenantProfileImage: string | null;
  language: string;
  industry: string | null;
  profession: string | null;
  platformRole: "root" | "root_admin" | null;
  isNotary: boolean;
  isAccountant: boolean;
  /** Set for notary users: pending until platform activates the account. */
  notaryActivationStatus: "pending" | "active" | null;
  /** Set for accountant users: pending until platform approves certificate review. */
  accountantActivationStatus: "pending" | "active" | null;
  sessionId: string;
  onboardingCompletedAt: string | null;
  onboardingMode: "self" | "accountant" | null;
  hasDgiCredentials: boolean;
  emailVerifiedAt: string | null;
  phone: string | null;
  /**
   * Set the moment Twilio Verify approves the consumer's phone code.
   * Paired with `profileImage` to decide whether the post-payment
   * "complete profile" gate needs to fire after a successful certificate
   * request payment (see complete-profile-page).
   */
  phoneVerifiedAt: string | null;
  consumerAccountKind: ConsumerAccountKind | null;
  consumerCompanyRegistration: ConsumerCompanyRegistrationSnapshot | null;
  /** False until the user has set a password. Roots are forced to set one. */
  hasPassword: boolean;
}

interface AuthContextValue {
  user: AuthUser | null;
  isLoading: boolean;
  isAuthenticated: boolean;
  requestLoginLink: (email: string, locale?: string, returnTo?: string) => Promise<void>;
  verifyAndLogin: (
    token: string,
    activeTenantId?: string
  ) => Promise<void>;
  /** Accept platform (root / support) invite from magic link; no tenant on session. */
  verifyRootInviteAndLogin: (token: string) => Promise<void>;
  loginWithPassword: (
    email: string,
    password: string,
    locale?: string,
    adminLogin?: boolean
  ) => Promise<LoginWithPasswordResponse>;
  submitPlatformLoginOtp: (pendingLoginId: string, code: string) => Promise<void>;
  submitProfessionalTotp: (pendingLoginId: string, code: string) => Promise<void>;
  submitProfessionalTotpRecovery: (
    pendingLoginId: string,
    recoveryCode: string
  ) => Promise<void>;
  resendPlatformLoginOtp: (pendingLoginId: string, locale?: string) => Promise<void>;
  registerAndLogin: (
    email: string,
    password: string,
    registrationToken: string,
    profile: {
      firstName: string;
      lastName: string;
      accountKind?: ConsumerAccountKind;
      companyRegistration?: RegisterCompanyRegistrationPayload;
    },
    captchaToken?: string
  ) => Promise<void>;
  exchangeSsoCodeAndLogin: (code: string) => Promise<void>;
  logout: () => Promise<void>;
  /** Clear token and user without calling the API (e.g. after account deletion). */
  clearLocalSession: () => void;
  refreshUser: () => Promise<void>;
  impersonate: (tenantId: string) => Promise<void>;
  endImpersonation: () => Promise<void>;
  /** Apply select-tenant response (token + user state). Call after selectTenant(tenantId). */
  applySelectTenantResponse: (response: VerifyResponse) => Promise<void>;
  /** Create company from RUT (user with no tenant). Applies new session and updates user state. */
  createCompanyAndSetUser: (
    rut: string,
    companyData?: {
      legalName?: string;
      address?: string;
      city?: string;
      department?: string;
      phone?: string;
      contactEmail?: string;
      industry?: string;
      profession?: string;
    }
  ) => Promise<void>;
  /**
   * The invitation behind an acceptance link, for ANY profession — the
   * profession is resolved from the token, never named by the caller.
   */
  getProfessionalInvitationByToken: (
    token: string
  ) => Promise<ProfessionalInvitationPayload>;
  /**
   * ACCEPTANCE, for any profession in the registry — ONE wrapper where there
   * were two.
   *
   * The two it replaces were written differently: the notary one forwarded
   * `params` whole, the accountant one rebuilt the body field by field, and
   * that difference is the documented reason `coverageAreas` reached the
   * server as `undefined` on the accountant path while working on the other.
   * One wrapper that forwards whole cannot develop that asymmetry again, and
   * accept-wrapper-forwards-every-field.spec.ts holds it to it.
   */
  acceptProfessionalInviteAndLogin: (
    token: string,
    params: {
      cedula: string;
      /**
       * The profession's own credential — a matrícula, a registration number,
       * nothing at all. Named for its ROLE rather than for one profession's
       * word, and optional because whether one is required is the
       * verification strategy's refusal to make (decision a78bb4f).
       */
      credential?: string;
      firstName: string;
      lastName: string;
      password: string;
      profileImageFile?: File;
      phone: string;
      /** The fiscal declaration — written as the user row is created. */
      rut?: string;
      razonSocial?: string;
      issuesCfe?: boolean;
      /** Departments covered; asked at acceptance (task 4228162). */
      coverageAreas?: string[];
    }
  ) => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }): JSX.Element {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  const refreshUser = useCallback(async (): Promise<void> => {
    if (!hasStoredToken()) {
      setUser(null);
      setIsLoading(false);
      return;
    }
    try {
      const session = await getSession();
      setUser(authUserFromSession(session));
    } catch {
      setUser(null);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    void refreshUser();
  }, [refreshUser]);

  /**
   * Same fetch as refreshUser, but a failure leaves the current user in
   * place: this runs in the background, and a flaky network must not look
   * like a logout.
   */
  const revalidateInFlight = useRef(false);
  const revalidateUser = useCallback(async (): Promise<void> => {
    // Returning to a backgrounded tab fires `focus` and `visibilitychange`
    // back to back; without this guard both would issue a session fetch and
    // the slower response would win.
    if (!hasStoredToken() || revalidateInFlight.current) {
      return;
    }
    revalidateInFlight.current = true;
    try {
      const session = await getSession();
      setUser((current) => {
        if (current == null) {
          return current;
        }
        const next = authUserFromSession(session);
        // Keep the same object when nothing moved, so a quiet poll does not
        // re-render every consumer of this context.
        return JSON.stringify(next) === JSON.stringify(current) ? current : next;
      });
    } catch {
      // Keep the last known session; the next tick will try again.
    } finally {
      revalidateInFlight.current = false;
    }
  }, []);

  const pendingActivation = isPendingProfessionalActivation(user);

  useEffect(() => {
    if (!pendingActivation) {
      return;
    }
    const revalidateIfVisible = (): void => {
      if (document.visibilityState === "hidden") {
        return;
      }
      void revalidateUser();
    };
    const interval = window.setInterval(
      revalidateIfVisible,
      PENDING_ACTIVATION_POLL_MS,
    );
    window.addEventListener("focus", revalidateIfVisible);
    document.addEventListener("visibilitychange", revalidateIfVisible);
    return () => {
      window.clearInterval(interval);
      window.removeEventListener("focus", revalidateIfVisible);
      document.removeEventListener("visibilitychange", revalidateIfVisible);
    };
  }, [pendingActivation, revalidateUser]);

  const requestLoginLink = useCallback(
    async (email: string, locale?: string, returnTo?: string): Promise<void> => {
      await requestLoginLinkApi({ email, locale, returnTo });
    },
    []
  );

  const verifyAndLogin = useCallback(
    async (token: string, activeTenantId?: string): Promise<void> => {
      const response = await verifyApi({ token, activeTenantId });
      applyVerifyResponse(response);
      const session = await getSession();
      setUser({
        id: session.userId,
        email: response.user.email,
        displayName: response.user.displayName ?? null,
        firstName: response.user.firstName ?? null,
        lastName: response.user.lastName ?? null,
        secondName: session.secondName ?? null,
        secondLastName: session.secondLastName ?? null,
        documentType: session.documentType ?? null,
        documentNumber: session.documentNumber ?? null,
        identityVerifiedAt: session.identityVerifiedAt ?? null,
        profileImage: response.user.profileImage ?? null,
        role: session.role,
        tenantId: session.activeTenantId,
        tenantName: session.tenantName,
        tenantProfileImage: session.tenantProfileImage ?? null,
        language: session.language,
        industry: session.industry,
        profession: session.profession ?? null,
        platformRole: session.platformRole,
        isNotary: session.isNotary ?? false,
        isAccountant: session.isAccountant ?? response.user.isAccountant ?? false,
        notaryActivationStatus: session.notaryActivationStatus ?? null,
        accountantActivationStatus: session.accountantActivationStatus ?? null,
        sessionId: session.sessionId,
        onboardingCompletedAt: session.onboardingCompletedAt ?? null,
        onboardingMode: session.onboardingMode ?? null,
        hasDgiCredentials: session.hasDgiCredentials ?? false,
        emailVerifiedAt: response.user.emailVerifiedAt ?? session.emailVerifiedAt ?? null,
        phone: session.phone ?? null,
        ...consumerFieldsFromSession(session),
        hasPassword: session.hasPassword ?? true,
      });
      setIsLoading(false);
    },
    []
  );

  const verifyRootInviteAndLogin = useCallback(async (token: string): Promise<void> => {
    const response = await verifyRootInviteApi({ token });
    applyVerifyResponse(response);
    const session = await getSession();
    setUser({
      id: session.userId,
      email: response.user.email,
      displayName: response.user.displayName ?? null,
      firstName: response.user.firstName ?? null,
      lastName: response.user.lastName ?? null,
      secondName: session.secondName ?? null,
      secondLastName: session.secondLastName ?? null,
      documentType: session.documentType ?? null,
      documentNumber: session.documentNumber ?? null,
      identityVerifiedAt: session.identityVerifiedAt ?? null,
      profileImage: response.user.profileImage ?? null,
      role: session.role,
      tenantId: session.activeTenantId,
      tenantName: session.tenantName,
      tenantProfileImage: session.tenantProfileImage ?? null,
      language: session.language,
      industry: session.industry,
      profession: session.profession ?? null,
      platformRole: session.platformRole,
      isNotary: session.isNotary ?? false,
      isAccountant: session.isAccountant ?? response.user.isAccountant ?? false,
      notaryActivationStatus: session.notaryActivationStatus ?? null,
      accountantActivationStatus: session.accountantActivationStatus ?? null,
      sessionId: session.sessionId,
      onboardingCompletedAt: session.onboardingCompletedAt ?? null,
      onboardingMode: session.onboardingMode ?? null,
      hasDgiCredentials: session.hasDgiCredentials ?? false,
      emailVerifiedAt: response.user.emailVerifiedAt ?? session.emailVerifiedAt ?? null,
      phone: session.phone ?? null,
      ...consumerFieldsFromSession(session),
      hasPassword: session.hasPassword ?? true,
    });
    setIsLoading(false);
  }, []);

  const finalizePasswordLoginSession = useCallback(async (response: VerifyResponse): Promise<void> => {
    applyVerifyResponse(response);
    const session = await getSession();
    setUser({
      id: session.userId,
      email: response.user.email,
      displayName: response.user.displayName ?? null,
      firstName: response.user.firstName ?? null,
      lastName: response.user.lastName ?? null,
      secondName: session.secondName ?? null,
      secondLastName: session.secondLastName ?? null,
      documentType: session.documentType ?? null,
      documentNumber: session.documentNumber ?? null,
      identityVerifiedAt: session.identityVerifiedAt ?? null,
      profileImage: response.user.profileImage ?? null,
      role: session.role,
      tenantId: session.activeTenantId,
      tenantName: session.tenantName,
      tenantProfileImage: session.tenantProfileImage ?? null,
      language: session.language,
      industry: session.industry,
      profession: session.profession ?? null,
      platformRole: session.platformRole,
      isNotary: session.isNotary ?? false,
      isAccountant: session.isAccountant ?? response.user.isAccountant ?? false,
      notaryActivationStatus: session.notaryActivationStatus ?? null,
      accountantActivationStatus: session.accountantActivationStatus ?? null,
      sessionId: session.sessionId,
      onboardingCompletedAt: session.onboardingCompletedAt ?? null,
      onboardingMode: session.onboardingMode ?? null,
      hasDgiCredentials: session.hasDgiCredentials ?? false,
      emailVerifiedAt: response.user.emailVerifiedAt ?? session.emailVerifiedAt ?? null,
      phone: session.phone ?? null,
      ...consumerFieldsFromSession(session),
      hasPassword: session.hasPassword ?? true,
    });
    setIsLoading(false);
  }, []);

  const loginWithPassword = useCallback(
    async (
      email: string,
      password: string,
      locale?: string,
      adminLogin?: boolean
    ): Promise<LoginWithPasswordResponse> => {
      const response = await loginWithPasswordApi({ email, password, locale, adminLogin });
      // Any CHALLENGE response carries no tokens, so it must be handed back to
      // the form untouched. Finalizing one would store a session that does not
      // exist. TypeScript caught this when the professional challenge was
      // added — the previous code narrowed out only the platform case.
      if (isPlatformOtpRequiredResponse(response) || isTotpRequiredResponse(response)) {
        return response;
      }
      await finalizePasswordLoginSession(response);
      return response;
    },
    [finalizePasswordLoginSession]
  );

  const submitPlatformLoginOtp = useCallback(
    async (pendingLoginId: string, code: string): Promise<void> => {
      const response = await completePlatformLoginOtpApi({ pendingLoginId, code });
      await finalizePasswordLoginSession(response);
    },
    [finalizePasswordLoginSession]
  );

  const submitProfessionalTotp = useCallback(
    async (pendingLoginId: string, code: string): Promise<void> => {
      const response = await completeProfessionalTotpLoginApi({ pendingLoginId, code });
      await finalizePasswordLoginSession(response);
    },
    [finalizePasswordLoginSession]
  );

  const submitProfessionalTotpRecovery = useCallback(
    async (pendingLoginId: string, recoveryCode: string): Promise<void> => {
      const response = await completeProfessionalTotpRecoveryLoginApi({
        pendingLoginId,
        recoveryCode,
      });
      await finalizePasswordLoginSession(response);
    },
    [finalizePasswordLoginSession]
  );

  const resendPlatformLoginOtp = useCallback(
    async (pendingLoginId: string, locale?: string): Promise<void> => {
      await resendPlatformLoginOtpApi({ pendingLoginId, locale });
    },
    []
  );

  const registerAndLogin = useCallback(
    async (
      email: string,
      password: string,
      registrationToken: string,
      profile: {
        firstName: string;
        lastName: string;
        accountKind?: ConsumerAccountKind;
        companyRegistration?: RegisterCompanyRegistrationPayload;
      },
      captchaToken?: string
    ): Promise<void> => {
      const response = await registerApi({
        firstName: profile.firstName,
        lastName: profile.lastName,
        email,
        password,
        registrationToken,
        ...(captchaToken != null && captchaToken !== "" && { captchaToken }),
        ...(profile.accountKind != null && { accountKind: profile.accountKind }),
        ...(profile.companyRegistration != null && {
          companyRegistration: profile.companyRegistration,
        }),
      });
      applyVerifyResponse(response);
      const session = await getSession();
      setUser({
        id: session.userId,
        email: response.user.email,
        displayName: response.user.displayName ?? null,
        firstName: response.user.firstName ?? null,
        lastName: response.user.lastName ?? null,
        secondName: session.secondName ?? null,
        secondLastName: session.secondLastName ?? null,
        documentType: session.documentType ?? null,
        documentNumber: session.documentNumber ?? null,
        identityVerifiedAt: session.identityVerifiedAt ?? null,
        profileImage: response.user.profileImage ?? null,
        role: session.role,
        tenantId: session.activeTenantId,
        tenantName: session.tenantName,
        tenantProfileImage: session.tenantProfileImage ?? null,
        language: session.language,
        industry: session.industry,
        profession: session.profession ?? null,
        platformRole: session.platformRole,
        isNotary: session.isNotary ?? false,
        isAccountant: session.isAccountant ?? response.user.isAccountant ?? false,
        notaryActivationStatus: session.notaryActivationStatus ?? null,
        accountantActivationStatus: session.accountantActivationStatus ?? null,
        sessionId: session.sessionId,
        onboardingCompletedAt: session.onboardingCompletedAt ?? null,
        onboardingMode: session.onboardingMode ?? null,
        hasDgiCredentials: session.hasDgiCredentials ?? false,
        emailVerifiedAt: response.user.emailVerifiedAt ?? session.emailVerifiedAt ?? null,
        phone: session.phone ?? null,
        ...consumerFieldsFromSession(session),
        hasPassword: session.hasPassword ?? true,
      });
      setIsLoading(false);
    },
    []
  );

  const exchangeSsoCodeAndLogin = useCallback(
    async (code: string): Promise<void> => {
      const response = await exchangeSsoCodeApi(code);
      applyVerifyResponse(response);
      const session = await getSession();
      setUser({
        id: session.userId,
        email: response.user.email,
        displayName: response.user.displayName ?? null,
        firstName: response.user.firstName ?? null,
        lastName: response.user.lastName ?? null,
        secondName: session.secondName ?? null,
        secondLastName: session.secondLastName ?? null,
        documentType: session.documentType ?? null,
        documentNumber: session.documentNumber ?? null,
        identityVerifiedAt: session.identityVerifiedAt ?? null,
        profileImage: response.user.profileImage ?? null,
        role: session.role,
        tenantId: session.activeTenantId,
        tenantName: session.tenantName,
        tenantProfileImage: session.tenantProfileImage ?? null,
        language: session.language,
        industry: session.industry,
        profession: session.profession ?? null,
        platformRole: session.platformRole,
        isNotary: session.isNotary ?? false,
        isAccountant: session.isAccountant ?? response.user.isAccountant ?? false,
        notaryActivationStatus: session.notaryActivationStatus ?? null,
        accountantActivationStatus: session.accountantActivationStatus ?? null,
        sessionId: session.sessionId,
        onboardingCompletedAt: session.onboardingCompletedAt ?? null,
        onboardingMode: session.onboardingMode ?? null,
        hasDgiCredentials: session.hasDgiCredentials ?? false,
        emailVerifiedAt: response.user.emailVerifiedAt ?? session.emailVerifiedAt ?? null,
        phone: session.phone ?? null,
        ...consumerFieldsFromSession(session),
        hasPassword: session.hasPassword ?? true,
      });
      setIsLoading(false);
    },
    []
  );

  const logout = useCallback(async (): Promise<void> => {
    const sessionId = user?.sessionId;
    try {
      if (sessionId) {
        await logoutApi({ sessionId });
      } else {
        await logoutApi();
      }
    } finally {
      setUser(null);
    }
  }, [user?.sessionId]);

  const clearLocalSession = useCallback((): void => {
    clearAuth();
    setUser(null);
  }, []);

  const impersonate = useCallback(async (tenantId: string): Promise<void> => {
    const response = await impersonateApi(tenantId);
    applyImpersonateResponse(response);
    await refreshUser();
  }, [refreshUser]);

  const endImpersonation = useCallback(async (): Promise<void> => {
    const response = await endImpersonationApi();
    applyImpersonateResponse(response);
    await refreshUser();
  }, [refreshUser]);

  const applySelectTenantResponse = useCallback(
    async (response: VerifyResponse): Promise<void> => {
      applyVerifyResponse(response);
      const session = await getSession();
      setUser({
        id: session.userId,
        email: response.user.email,
        displayName: response.user.displayName ?? null,
        firstName: response.user.firstName ?? null,
        lastName: response.user.lastName ?? null,
        secondName: session.secondName ?? null,
        secondLastName: session.secondLastName ?? null,
        documentType: session.documentType ?? null,
        documentNumber: session.documentNumber ?? null,
        identityVerifiedAt: session.identityVerifiedAt ?? null,
        profileImage: response.user.profileImage ?? null,
        role: session.role,
        tenantId: session.activeTenantId,
        tenantName: session.tenantName,
        tenantProfileImage: session.tenantProfileImage ?? null,
        language: session.language,
        industry: session.industry,
        profession: session.profession ?? null,
        platformRole: session.platformRole,
        isNotary: session.isNotary ?? false,
        isAccountant: session.isAccountant ?? response.user.isAccountant ?? false,
        notaryActivationStatus: session.notaryActivationStatus ?? null,
        accountantActivationStatus: session.accountantActivationStatus ?? null,
        sessionId: session.sessionId,
        onboardingCompletedAt: session.onboardingCompletedAt ?? null,
        onboardingMode: session.onboardingMode ?? null,
        hasDgiCredentials: session.hasDgiCredentials ?? false,
        emailVerifiedAt: response.user.emailVerifiedAt ?? session.emailVerifiedAt ?? null,
        phone: session.phone ?? null,
        ...consumerFieldsFromSession(session),
        hasPassword: session.hasPassword ?? true,
      });
    },
    []
  );

  const createCompanyAndSetUser = useCallback(
    async (
      rut: string,
      companyData?: {
        legalName?: string;
        address?: string;
        city?: string;
        department?: string;
        phone?: string;
        contactEmail?: string;
        industry?: string;
      }
    ): Promise<void> => {
      const response = await createCompanyApi({ rut, ...companyData });
      applyVerifyResponse(response);
      const session = await getSession();
      setUser({
        id: session.userId,
        email: response.user.email,
        displayName: response.user.displayName ?? null,
        firstName: response.user.firstName ?? null,
        lastName: response.user.lastName ?? null,
        secondName: session.secondName ?? null,
        secondLastName: session.secondLastName ?? null,
        documentType: session.documentType ?? null,
        documentNumber: session.documentNumber ?? null,
        identityVerifiedAt: session.identityVerifiedAt ?? null,
        profileImage: response.user.profileImage ?? null,
        role: session.role,
        tenantId: session.activeTenantId,
        tenantName: session.tenantName,
        tenantProfileImage: session.tenantProfileImage ?? null,
        language: session.language,
        industry: session.industry,
        profession: session.profession ?? null,
        platformRole: session.platformRole,
        isNotary: session.isNotary ?? false,
        isAccountant: session.isAccountant ?? response.user.isAccountant ?? false,
        notaryActivationStatus: session.notaryActivationStatus ?? null,
        accountantActivationStatus: session.accountantActivationStatus ?? null,
        sessionId: session.sessionId,
        onboardingCompletedAt: session.onboardingCompletedAt ?? null,
        onboardingMode: session.onboardingMode ?? null,
        hasDgiCredentials: session.hasDgiCredentials ?? false,
        emailVerifiedAt: response.user.emailVerifiedAt ?? session.emailVerifiedAt ?? null,
        phone: session.phone ?? null,
        ...consumerFieldsFromSession(session),
        hasPassword: session.hasPassword ?? true,
      });
    },
    []
  );

  const getProfessionalInvitationByToken = useCallback(
    (token: string) => getProfessionalInvitationByTokenApi(token),
    []
  );

  const acceptProfessionalInviteAndLogin = useCallback(
    async (
      token: string,
      params: {
        cedula: string;
        credential?: string;
        firstName: string;
        lastName: string;
        password: string;
        profileImageFile?: File;
        phone: string;
        rut?: string;
        razonSocial?: string;
        issuesCfe?: boolean;
        coverageAreas?: string[];
      }
    ): Promise<void> => {
      // `params` is forwarded WHOLE. The service builds the multipart body and
      // decides what is absent; nothing is named twice, so a field added to
      // an acceptance page reaches the server without a change here.
      const response = await acceptProfessionalInviteApi(token, params);
      applyVerifyResponse(response);
      const session = await getSession();
      setUser({
        id: session.userId,
        email: response.user.email,
        displayName: response.user.displayName ?? null,
        firstName: response.user.firstName ?? null,
        lastName: response.user.lastName ?? null,
        secondName: session.secondName ?? null,
        secondLastName: session.secondLastName ?? null,
        documentType: session.documentType ?? null,
        documentNumber: session.documentNumber ?? null,
        identityVerifiedAt: session.identityVerifiedAt ?? null,
        /*
          The avatar comes back ON the acceptance response: this route is
          multipart and carried the file, so there is no second upload call to
          wait for the way the accountant path used to need one.
        */
        profileImage: session.profileImage ?? response.user.profileImage ?? null,
        role: session.role,
        tenantId: session.activeTenantId,
        tenantName: session.tenantName,
        tenantProfileImage: session.tenantProfileImage ?? null,
        language: session.language,
        industry: session.industry,
        profession: session.profession ?? null,
        platformRole: session.platformRole,
        /*
          READ, NOT ASSERTED. The two wrappers this replaces hardcoded
          `isNotary: true` and `isAccountant: true` — which a third profession
          would have made a lie. The flags survive in the API for shipped
          mobile builds (decision D5) and the backend derives them from the
          invite's own slug, so a profession that is neither answers false to
          both. Nothing here may DECIDE anything from them.
        */
        isNotary: session.isNotary ?? response.user.isNotary ?? false,
        isAccountant: session.isAccountant ?? response.user.isAccountant ?? false,
        notaryActivationStatus:
          session.notaryActivationStatus ?? response.user.notaryActivationStatus ?? null,
        accountantActivationStatus:
          response.user.accountantActivationStatus ??
          session.accountantActivationStatus ??
          null,
        sessionId: session.sessionId,
        onboardingCompletedAt: session.onboardingCompletedAt ?? null,
        onboardingMode: session.onboardingMode ?? null,
        hasDgiCredentials: session.hasDgiCredentials ?? false,
        emailVerifiedAt: response.user.emailVerifiedAt ?? session.emailVerifiedAt ?? null,
        phone: session.phone ?? response.user.phone ?? null,
        ...consumerFieldsFromSession(session),
        hasPassword: session.hasPassword ?? true,
      });
      setIsLoading(false);
    },
    []
  );

  const value: AuthContextValue = {
    user,
    isLoading,
    isAuthenticated: user != null,
    requestLoginLink,
    verifyAndLogin,
    verifyRootInviteAndLogin,
    loginWithPassword,
    submitPlatformLoginOtp,
    submitProfessionalTotp,
    submitProfessionalTotpRecovery,
    resendPlatformLoginOtp,
    registerAndLogin,
    exchangeSsoCodeAndLogin,
    logout,
    clearLocalSession,
    refreshUser,
    impersonate,
    endImpersonation,
    applySelectTenantResponse,
    createCompanyAndSetUser,
    getProfessionalInvitationByToken,
    acceptProfessionalInviteAndLogin,
  };

  return (
    <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
  );
}

export function useAuth(): AuthContextValue {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
}

/**
 * Same as useAuth but returns undefined when used outside AuthProvider.
 * Use only for optional UI (e.g. document title) that should not throw during HMR or before provider mount.
 */
export function useAuthOptional(): AuthContextValue | undefined {
  return useContext(AuthContext);
}
